/** Frames the height must hold still before a pin releases. */
const DEFAULT_QUIET_FRAMES = 3
/** Safety valve: content that never settles must not pin the scroller forever. */
const DEFAULT_MAX_MS = 2000

export interface PinToBottomOptions {
  quietFrames?: number
  maxMs?: number
  /**
   * Hold the window open at least this long, even once the height goes quiet.
   * A view that opens against content still arriving goes quiet between chunks
   * — releasing there hands the rest of the backlog to a *smooth* catch-up, so
   * the open snaps and then visibly glides.
   */
  minMs?: number
  /** Return false to abandon the pin — e.g. the user has taken over scrolling. */
  shouldContinue?: () => boolean
  /** Runs on every pin, including the first. Sync dependent state here. */
  onPin?: (element: HTMLElement) => void
  /** Runs exactly once when the window ends, however it ends. */
  onRelease?: () => void
}

/**
 * Snap `element` to its bottom and hold it there while the content settles.
 *
 * A view rarely has its content on the first commit — messages arrive from the
 * network after mount, transcript buffers are refetched over IPC, editors collapse
 * for a beat, text reflows once fonts land. A single jump therefore lands against
 * the wrong height, and the real content shows up underneath a stale scroll offset:
 * either the view visibly scrolls itself down afterwards, or it just sits at the
 * top of a long history. So the pin persists — any growth over the next few frames
 * is re-pinned instantly, and the window ends once the height holds still, so
 * later content still animates in normally.
 *
 * Returns a release function; calling it ends the window early.
 */
export function pinElementToBottom(element: HTMLElement, options: PinToBottomOptions = {}): () => void {
  const {
    quietFrames: maxQuietFrames = DEFAULT_QUIET_FRAMES,
    maxMs = DEFAULT_MAX_MS,
    minMs = 0,
    shouldContinue,
    onPin,
    onRelease,
  } = options

  const pin = () => {
    element.scrollTo({ top: element.scrollHeight, behavior: 'instant' })
    onPin?.(element)
  }

  let frame: number | null = null
  let released = false

  const release = () => {
    if (released) return
    released = true
    if (frame !== null) cancelAnimationFrame(frame)
    frame = null
    onRelease?.()
  }

  pin()

  const startedAt = performance.now()
  let lastHeight = element.scrollHeight
  let quiet = 0

  const tick = () => {
    frame = null
    if (shouldContinue && !shouldContinue()) return release()

    const height = element.scrollHeight
    if (height !== lastHeight) {
      lastHeight = height
      quiet = 0
      pin()
    } else {
      quiet += 1
    }

    const elapsed = performance.now() - startedAt
    if ((quiet > maxQuietFrames && elapsed >= minMs) || elapsed > maxMs) return release()
    frame = requestAnimationFrame(tick)
  }

  frame = requestAnimationFrame(tick)

  return release
}
